import { WttpQuestionnairesTable, QuestionnairesSchedule } from './table/table';
import { useQuery } from '@apollo/client';
import {
  WttpQuestionnaire,
  WttpQuestionnairesDocument,
  WttpQuestionnairesQuery,
  WttpTemplateListFilterInput,
} from 'src/graphql/generated/graphql';
import { useTable } from 'src/hooks/use-table';
import { useState } from 'react';
import { Typography } from '@otp/ui';

export type { QuestionnairesSchedule } from './table/table';

const PAGINATION_LIMIT = 5;

type TemplateQuestionnaireListProps = {
  value: QuestionnairesSchedule[];
  onChange: (value: QuestionnairesSchedule[]) => void;
  error?: string;
  wttpQuestionnaires?: Pick<
    WttpQuestionnaire,
    'id' | 'name' | 'organizationId'
  >[];
  selectableRows?: 'single' | 'multiple' | 'none';
  loading?: boolean;
};

export const TemplateQuestionnaireList = (
  props: TemplateQuestionnaireListProps,
) => {
  const {
    value,
    onChange,
    error,
    wttpQuestionnaires: wttpQuestionnairesProp,
    selectableRows = 'multiple',
    loading: loadingProp,
  } = props;

  const { page, setPage, filter, onFilterChange, defaultFilter } = useTable<
    WttpTemplateListFilterInput,
    Record<any, any>
  >();
  const [totalCount, setTotalCount] = useState(
    wttpQuestionnairesProp?.length || 0,
  );

  const { loading, data } = useQuery<WttpQuestionnairesQuery>(
    WttpQuestionnairesDocument,
    {
      variables: {
        filter: {},
        sorting: {},
        pagination: {
          limit: PAGINATION_LIMIT,
          offset: page * PAGINATION_LIMIT,
        },
      },
      onCompleted: (data) =>
        setTotalCount(data.wttpQuestionnaires.pagination.totalCount),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-only',
      skip: !!wttpQuestionnairesProp,
    },
  );
  const wttpQuestionnaires =
    wttpQuestionnairesProp || data?.wttpQuestionnaires?.records || [];

  const onScheduleChange = (data: QuestionnairesSchedule) => {
    const newValue = value?.map((item) =>
      item.questionnaireId === data.questionnaireId ? data : item,
    );

    onChange(newValue);
  };

  const onRowsSelected = (ids: string[]) => {
    const newValue = ids.reduce(
      (map, id) => [
        ...map,
        {
          questionnaireId: id,
          scheduleId: value?.find((item) => item.questionnaireId === id)
            ?.scheduleId,
        },
      ],
      [] as QuestionnairesSchedule[],
    );

    onChange(newValue);
  };

  return (
    <>
      <WttpQuestionnairesTable
        data={wttpQuestionnaires}
        options={{
          page,
          count: totalCount,
          sort: true,
          rowsPerPage: PAGINATION_LIMIT,
          onChangePage: setPage,
        }}
        selectableRows={selectableRows}
        loading={loadingProp || loading}
        onRowsSelected={onRowsSelected}
        onScheduleChange={onScheduleChange}
        selectedRowIds={value?.map((item) => item.questionnaireId)}
        value={value}
      />
      {error && (
        <Typography variant="subtitle1" color="red" textAlign="right">
          {error}
        </Typography>
      )}
    </>
  );
};
